import React from "react";
import Seo from "../components/Seo.jsx";

/* Mobile — real responsive shell (MobileApp) + desktop preview (MobileScreen).
   Tabs: Home / Opportunities / Scans / Settings. Each tab mirrors the desktop
   surface so a phone user can see scan summary, filter the feed, drill into
   past scans, and manage notifications + account from one place. */

// ────────────────────────────────────────────────────────────────────────────
// Shared mobile primitives
// ────────────────────────────────────────────────────────────────────────────

function MobileChrome({ title, subtitle, right, left, children, hideHeader = false }) {
  return (
    <div className="mobile-chrome">
      {!hideHeader && (
        <div className="mobile-chrome-header">
          {left}
          <div style={{ flex: 1, minWidth: 0 }}>
            {subtitle && (
              <div className="mobile-chrome-eyebrow">{subtitle}</div>
            )}
            <div className="mobile-chrome-title">{title}</div>
          </div>
          {right}
        </div>
      )}
      <div className="mobile-chrome-scroll">
        {children}
      </div>
    </div>
  );
}

function MobileStat({ label, value, meta, icon, accent }) {
  const IconCmp = icon ? window.I[icon] : null;
  return (
    <div className={"mobile-stat " + (accent ? "mobile-stat-accent" : "")}>
      <div className="mobile-stat-label">
        {IconCmp && <IconCmp size={12} />}
        {label}
      </div>
      <div className="mobile-stat-value">{value}</div>
      {meta && <div className="mobile-stat-meta">{meta}</div>}
    </div>
  );
}

function MobilePill({ tone = "neutral", children, style }) {
  return (
    <span className={"pill pill-" + tone} style={{ fontSize: 11, ...style }}>
      {children}
    </span>
  );
}

function isPreResultRunning(scan) {
  return (
    scan?.status === "running" &&
    Number(scan?.candidates_count || 0) === 0 &&
    Number(scan?.surfaced_count || 0) === 0
  );
}

function mobileBriefTierLabel(tier) {
  if (tier === "high") return "HIGH INTENT";
  if (tier === "medium") return "MEDIUM INTENT";
  if (tier === "new") return "WATCHING";
  return "LOW INTENT";
}

function MobileBriefRow({ opp, index = 0, onOpen }) {
  const itemNumber = String(index + 1).padStart(2, "0");
  const isCluster = opp.kind === "comment-cluster";
  const extra = isCluster
    ? `${opp.memberCount} matched comments in this thread`
    : null;
  const title = isCluster ? `Comments on: ${opp.title}` : opp.title;

  return (
    <button
      type="button"
      className="mobile-brief-row"
      onClick={() => onOpen?.(isCluster ? opp : opp.id)}
    >
      <div className="mobile-brief-num">
        {opp.isNew && <span className="mobile-brief-newdot" />}
        {itemNumber}
      </div>
      <div className="mobile-brief-body">
        <div className="mobile-brief-kicker">
          <span className={"mobile-brief-tag tier-" + (opp.tier === "medium" ? "med" : opp.tier)}>
            {mobileBriefTierLabel(opp.tier)}
          </span>
          <span className="mobile-brief-sub">{opp.subreddit}</span>
          <span className="mobile-brief-sep">·</span>
          <span className="mobile-brief-ago">{opp.posted}</span>
        </div>
        <div className="mobile-brief-title">{title}</div>
        {extra && <div className="mobile-brief-extra">{extra}</div>}
        <div className="mobile-brief-foot">
          <span className="mobile-brief-foot-score">
            {opp.score}% {isCluster ? "top match" : "match"}
          </span>
          <span className="mobile-brief-foot-sep">·</span>
          <span>{opp.comments || 0} comments</span>
          <span className="mobile-brief-foot-sep">·</span>
          <span>{opp.upvotes || 0} upvotes</span>
        </div>
      </div>
      <div className="mobile-brief-arrow" aria-hidden="true">
        <window.I.ArrowUpRight size={14} />
      </div>
    </button>
  );
}

function MobileDemoExitButton({ isDemo, onExitDemo }) {
  if (!isDemo || !onExitDemo) return null;
  return (
    <button type="button" className="btn btn-ghost mobile-demo-exit" onClick={onExitDemo}>
      <window.I.ArrowLeft size={13} />
      Exit demo
    </button>
  );
}

function MobileProfileSwitcher({
  profile,
  profiles = [],
  profilesLoading,
  onSwitchProfile,
  onNewProfile,
  isDemo,
}) {
  const [open, setOpen] = React.useState(false);
  const ref = React.useRef(null);

  React.useEffect(() => {
    const onDoc = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const activeProfile = profile || profiles[0] || null;

  return (
    <>
      {open && (
        <div
          className="profile-pop-backdrop"
          onClick={() => setOpen(false)}
          aria-hidden="true"
        />
      )}
      <div
        ref={ref}
        className={"profile-selector-wrap mobile-profile-switcher " + (open ? "is-open" : "")}
      >
        <button
          type="button"
          className="mobile-profile-switcher-btn"
          onClick={() => setOpen((v) => !v)}
          aria-label={
            profilesLoading
              ? "Loading profiles"
              : `Switch profile, current: ${activeProfile?.name || "No profile"}`
          }
          aria-expanded={open}
          aria-haspopup="listbox"
        >
          <div
            className="profile-mark mobile-profile-switcher-mark"
            style={!activeProfile?.faviconUrl ? { background: activeProfile?.color || "var(--accent)" } : undefined}
          >
            {profilesLoading ? (
              <window.I.Refresh size={12} style={{ animation: "spin 900ms linear infinite" }} />
            ) : activeProfile?.faviconUrl ? (
              <img className="profile-mark-img" src={activeProfile.faviconUrl} alt="" />
            ) : (
              activeProfile?.initials || "N"
            )}
          </div>
          <window.I.Chevron size={12} style={{ color: "var(--text-tertiary)", flexShrink: 0 }} />
        </button>

        {open && (
          <div className="profile-pop mobile-profile-pop" role="listbox">
            {profilesLoading ? (
              <div className="item" style={{ color: "var(--text-secondary)" }}>
                <window.I.Refresh size={13} style={{ animation: "spin 900ms linear infinite" }} />
                loading profiles..
              </div>
            ) : profiles.length ? (
              profiles.map((p) => (
                <div
                  key={p.id}
                  role="option"
                  aria-selected={p.id === activeProfile?.id}
                  className={"item " + (p.id === activeProfile?.id ? "is-active" : "")}
                  onClick={() => {
                    onSwitchProfile?.(p.id);
                    setOpen(false);
                  }}
                >
                  <div
                    className="profile-mark"
                    style={
                      !p.faviconUrl
                        ? { background: p.color, width: 26, height: 26, fontSize: 11.5, borderRadius: 7 }
                        : { width: 26, height: 26, borderRadius: 7 }
                    }
                  >
                    {p.faviconUrl ? (
                      <img className="profile-mark-img" src={p.faviconUrl} alt="" />
                    ) : (
                      p.initials
                    )}
                  </div>
                  <div className="profile-item-body">
                    <div className="profile-item-name">{p.name}</div>
                    <div className="meta">{p.website}</div>
                  </div>
                  {p.id === activeProfile?.id && (
                    <window.I.Check size={14} style={{ color: "var(--accent)" }} />
                  )}
                </div>
              ))
            ) : (
              <div className="item" style={{ color: "var(--text-secondary)" }}>
                <window.I.Briefcase size={13} />
                {isDemo ? "Demo workspace · preview only" : "No business profiles yet"}
              </div>
            )}
            {!isDemo && onNewProfile && (
              <>
                <div className="sep" />
                <div
                  className="item add-new"
                  onClick={() => {
                    setOpen(false);
                    onNewProfile();
                  }}
                >
                  <div
                    style={{
                      width: 26,
                      height: 26,
                      borderRadius: 7,
                      border: "1px dashed var(--border-strong)",
                      display: "grid",
                      placeItems: "center",
                      color: "var(--text-tertiary)",
                    }}
                  >
                    <window.I.Plus size={14} />
                  </div>
                  <div>New business profile</div>
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Mobile Dashboard
// ────────────────────────────────────────────────────────────────────────────

function MobileDashboard({
  opps,
  oppsLoading,
  profile,
  profiles,
  profilesLoading,
  onSwitchProfile,
  onNewProfile,
  isDemo,
  latestScan,
  onOpenOpp,
  onNavigate,
  onScanNow,
  isScanning,
  firstScan,
}) {
  const newC = opps.filter((o) => o.isNew && o.feedback !== "not_relevant").length;
  const highC = opps.filter((o) => o.tier === "high").length;
  const reviewedC = opps.filter((o) => o.feedback).length;
  const monitored = (profile?.subreddits?.length) || 0;

  const lastScanLabel = React.useMemo(() => {
    const iso = latestScan?.finished_at || latestScan?.started_at;
    return iso ? window.relativeAgo(iso) : (profile?.lastScan || "—");
  }, [latestScan, profile]);

  const hour = new Date().getHours();
  const greeting =
    hour < 12 ? "Good morning" :
      hour < 18 ? "Good afternoon" :
        "Good evening";

  const scanCandidates = Number(latestScan?.candidates_count || 0);
  const scanSurfaced = Number(latestScan?.surfaced_count || 0);
  const showQuietBanner = latestScan && scanSurfaced === 0;
  const topOpps = React.useMemo(() => {
    const clustered = window.clusterOpps ? window.clusterOpps(opps) : opps;
    return clustered.slice(0, 6);
  }, [opps]);

  // Activity events
  const activityEvents = React.useMemo(() => {
    const out = [];
    if (latestScan) {
      const iso = latestScan.finished_at || latestScan.started_at;
      out.push({
        t: iso ? window.relativeAgo(iso) : "Recently",
        text: `Scan ${latestScan.status || "complete"} — ${
          latestScan.candidates_count ?? "?"
        } candidates, ${latestScan.surfaced_count ?? 0} surfaced`,
        icon: "Refresh",
      });
    }
    if (reviewedC > 0) {
      out.push({
        t: "This session",
        text: `Marked ${reviewedC} opportunit${reviewedC === 1 ? "y" : "ies"}`,
        icon: "ThumbsUp",
      });
    }
    if (out.length === 0 && !oppsLoading) {
      out.push({
        t: "Now",
        text: "Ready for your first scan. Tap Scans to run one.",
        icon: "Sparkles",
      });
    }
    return out;
  }, [latestScan, oppsLoading, reviewedC]);

  return (
    <MobileChrome
      title={greeting}
      subtitle="Community Ninja"
      right={
        <MobileProfileSwitcher
          profile={profile}
          profiles={profiles}
          profilesLoading={profilesLoading}
          onSwitchProfile={onSwitchProfile}
          onNewProfile={onNewProfile}
          isDemo={isDemo}
        />
      }
    >
      <div className={"mobile-page-wrap" + (firstScan ? " is-fresh" : "")}>
        <div className="mobile-page">
          <div className="mobile-hero">
            <div className="mobile-hero-eyebrow">
              Last scan · {lastScanLabel}
            </div>
            <div className="mobile-hero-title">
              {newC} new {newC === 1 ? "opportunity" : "opportunities"}
            </div>
            <div className="mobile-hero-sub">
              {opps.length > 0
                ? `${opps.length} active across ${monitored} subreddit${monitored === 1 ? "" : "s"} · ${highC} high match.`
                : oppsLoading
                  ? "Checking your workspace..."
                  : "Run your first scan to populate your radar."}
            </div>
          </div>

          {showQuietBanner && (
            <div className="mobile-scan-summary" role="status">
              <window.I.ShieldCheck size={13} />
              <span>
                Latest scan checked {scanCandidates.toLocaleString()} candidate{scanCandidates === 1 ? "" : "s"} and surfaced 0 new.
                {opps.length > 0 ? " Your existing inbox is still below." : ""}
              </span>
            </div>
          )}

          <div className="mobile-section-head">
            <div className="mobile-section-label">Top opportunities</div>
            <button
              className="mobile-section-link"
              onClick={() => onNavigate?.("opportunities")}
            >
              View all <window.I.ChevronRight size={11} />
            </button>
          </div>

          <div className="mobile-brief-list">
            {topOpps.map((o, i) => (
              <MobileBriefRow
                key={o.id}
                opp={o}
                index={i}
                onOpen={onOpenOpp}
              />
            ))}
            {opps.length === 0 && (
              <div className="mobile-empty-inline">
                No opportunities yet. Run a scan from the Scans tab.
              </div>
            )}
          </div>

          <div className="mobile-section-label" style={{ marginTop: 22 }}>Recent activity</div>
          <div className="mobile-info-card mobile-activity">
            {activityEvents.map((e, i) => {
              const Ic = window.I[e.icon];
              return (
                <div key={i} className="mobile-activity-row">
                  <div className="mobile-activity-icon"><Ic size={11} /></div>
                  <div className="mobile-activity-body">
                    <div className="mobile-activity-text">{e.text}</div>
                    <div className="mobile-activity-time">{e.t}</div>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="mobile-trust-card">
            <div className="mobile-trust-head">
              <window.I.ShieldCheck size={13} style={{ color: "var(--success)" }} />
              <span>Read-only by design</span>
            </div>
            <div className="mobile-trust-desc">
              Community Ninja never posts, comments, DMs, or votes on your behalf.
              We surface; you decide.
            </div>
          </div>
        </div>

        {firstScan && (
          <div className="mobile-fresh-overlay-layer">
            <div className="fresh-overlay-card mobile-fresh-overlay-card" role="region" aria-label="Run your first scan">
              <div className="fresh-overlay-badge">
                <window.I.Sparkles size={13} />
                Fresh workspace
              </div>
              <h2 className="fresh-overlay-title">Run your first scan</h2>
              <p className="fresh-overlay-desc">
                We&rsquo;ll sweep {monitored > 0 ? <strong>{monitored} subreddit{monitored === 1 ? "" : "s"}</strong> : "your monitored subreddits"}
                {" "}and surface conversations worth your attention. Read only.
              </p>
              <button
                className="btn btn-primary fresh-overlay-cta"
                onClick={onScanNow}
                disabled={isScanning}
              >
                {isScanning ? (
                  <>
                    <window.I.Sparkles size={16} style={{ animation: "spin 900ms linear infinite" }} />
                    Scanning…
                  </>
                ) : (
                  <>
                    <window.I.Sparkles size={16} />
                    Scan now
                  </>
                )}
              </button>
              <div className="fresh-overlay-foot">
                <span className="fresh-overlay-foot-item">
                  <window.I.Clock size={12} />
                  ~60 seconds
                </span>
              </div>
            </div>
          </div>
        )}
      </div>
    </MobileChrome>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Mobile Feed / Opportunities
// ────────────────────────────────────────────────────────────────────────────

function MobileFeed({ state, setState, onTap, latestScan }) {
  const {
    opps, oppsLoading, scoreMode, sort, threshold,
    highOnly, query, subredditFilter, hideNotRelevant, searchScope, isScanning,
    onRefreshOpps,
  } = state;

  const [filterOpen, setFilterOpen] = React.useState(false);

  const setKey = (k) => (v) => setState((s) => ({ ...s, [k]: v }));

  const lastScanLabel = React.useMemo(() => {
    const iso = latestScan?.finished_at || latestScan?.started_at;
    return iso ? window.relativeAgo(iso) : "—";
  }, [latestScan]);

  const subreddits = React.useMemo(() => {
    const set = new Set(opps.map((o) => o.subreddit));
    return Array.from(set);
  }, [opps]);
  const hasOpps = opps.length > 0;
  const showFeedSkeleton = oppsLoading && !hasOpps;

  const filtered = React.useMemo(() => {
    let list = opps.filter((o) => o.score >= threshold);
    if (highOnly) list = list.filter((o) => o.tier === "high");
    if (subredditFilter !== "all") list = list.filter((o) => o.subreddit === subredditFilter);
    if (hideNotRelevant) list = list.filter((o) => o.feedback !== "not_relevant");
    const ss = searchScope === "post" || searchScope === "comment" ? searchScope : "all";
    if (ss !== "all") list = list.filter((o) => o.kind === ss);
    if (query.trim()) {
      const q = query.toLowerCase();
      list = list.filter((o) =>
        o.title.toLowerCase().includes(q) ||
        (o.why || "").toLowerCase().includes(q) ||
        o.subreddit.toLowerCase().includes(q)
      );
    }
    if (sort === "best") list = [...list].sort((a, b) => b.score - a.score);
    if (sort === "new") list = [...list].sort((a, b) => parseTime(a.posted) - parseTime(b.posted));
    if (sort === "discussed") list = [...list].sort((a, b) => b.comments - a.comments);
    return list;
  }, [opps, threshold, highOnly, subredditFilter, hideNotRelevant, searchScope, query, sort]);

  function parseTime(p) {
    const m = String(p || "").match(/(\d+)([hd])/);
    if (!m) return 0;
    const n = parseInt(m[1], 10);
    return m[2] === "h" ? n : n * 24;
  }

  const activeFilterCount =
    (highOnly ? 1 : 0) +
    (threshold > 60 ? 1 : 0) +
    (subredditFilter !== "all" ? 1 : 0) +
    (hideNotRelevant ? 1 : 0) +
    (searchScope && searchScope !== "all" ? 1 : 0);

  const sortLabels = { best: "Best", new: "Newest", discussed: "Discussed" };

  return (
    <MobileChrome
      title={isScanning ? "scanning..." : "Opportunities"}
      subtitle={isScanning ? "Scanning in background" : `Updated · ${lastScanLabel}`}
      right={
        <button
          className="btn btn-ghost btn-icon"
          style={{ width: 32, height: 32 }}
          aria-label="Refresh"
          onClick={() => onRefreshOpps?.()}
        >
          <window.I.Refresh
            size={15}
            style={isScanning ? { animation: "spin 900ms linear infinite" } : null}
          />
        </button>
      }
    >
      <div className="mobile-feed">
        <div className="mobile-search-row">
          <div className="mobile-search">
            <window.I.Search size={13} />
            <input
              placeholder="Search opportunities…"
              value={query}
              onChange={(e) => setKey("query")(e.target.value)}
            />
            {query && (
              <button
                type="button"
                className="mobile-search-clear"
                onClick={() => setKey("query")("")}
                aria-label="Clear"
              >
                <window.I.X size={11} />
              </button>
            )}
          </div>
          <button
            type="button"
            className={"mobile-filter-btn " + (filterOpen || activeFilterCount > 0 ? "is-active" : "")}
            onClick={() => setFilterOpen((v) => !v)}
            aria-label="Filters"
          >
            <window.I.Filter size={14} />
            {activeFilterCount > 0 && (
              <span className="mobile-filter-count">{activeFilterCount}</span>
            )}
          </button>
        </div>

        {filterOpen && (
          <div className="mobile-filter-panel">
            <div className="mobile-filter-group">
              <div className="mobile-filter-label">Sort</div>
              <div className="mobile-chip-row mobile-chip-row-static">
                {[
                  { id: "best", label: "Best match" },
                  { id: "new", label: "Newest" },
                  { id: "discussed", label: "Most discussed" },
                ].map((opt) => (
                  <button
                    key={opt.id}
                    className={"pill " + (opt.id === sort ? "pill-accent" : "pill-neutral")}
                    onClick={() => setKey("sort")(opt.id)}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="mobile-filter-group">
              <div className="mobile-filter-label">Min match</div>
              <div className="mobile-chip-row mobile-chip-row-static">
                {[60, 75, 85, 90].map((t) => (
                  <button
                    key={t}
                    className={"pill " + (t === threshold ? "pill-accent" : "pill-neutral")}
                    onClick={() => setKey("threshold")(t)}
                  >
                    ≥ {t}%
                  </button>
                ))}
              </div>
            </div>

            <div className="mobile-filter-group">
              <div className="mobile-filter-label">Content</div>
              <div className="mobile-chip-row mobile-chip-row-static">
                {[
                  { id: "all", label: "All" },
                  { id: "post", label: "Posts" },
                  { id: "comment", label: "Comments" },
                ].map((opt) => (
                  <button
                    key={opt.id}
                    className={"pill " + ((searchScope || "all") === opt.id ? "pill-accent" : "pill-neutral")}
                    onClick={() => setKey("searchScope")(opt.id)}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>

            {subreddits.length > 0 && (
              <div className="mobile-filter-group">
                <div className="mobile-filter-label">Subreddit</div>
                <div className="mobile-chip-row mobile-chip-row-scroll">
                  <button
                    className={"pill " + (subredditFilter === "all" ? "pill-accent" : "pill-neutral")}
                    onClick={() => setKey("subredditFilter")("all")}
                  >
                    All
                  </button>
                  {subreddits.map((s) => (
                    <button
                      key={s}
                      className={"pill " + (s === subredditFilter ? "pill-accent" : "pill-neutral")}
                      onClick={() => setKey("subredditFilter")(s)}
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div className="mobile-filter-toggles">
              <button
                className={"mobile-toggle-row " + (highOnly ? "is-active" : "")}
                onClick={() => setKey("highOnly")(!highOnly)}
              >
                <window.I.ShieldCheck size={12} />
                <span>High confidence only</span>
                <div className="mobile-toggle-dot" />
              </button>
              <button
                className={"mobile-toggle-row " + (hideNotRelevant ? "is-active" : "")}
                onClick={() => setKey("hideNotRelevant")(!hideNotRelevant)}
              >
                <window.I.Eye size={12} />
                <span>Hide dismissed</span>
                <div className="mobile-toggle-dot" />
              </button>
            </div>

            {activeFilterCount > 0 && (
              <button
                type="button"
                className="btn btn-ghost btn-sm mobile-filter-reset"
                onClick={() => setState((s) => ({
                  ...s,
                  query: "",
                  subredditFilter: "all",
                  threshold: 0,
                  highOnly: false,
                  hideNotRelevant: false,
                  searchScope: "all",
                }))}
              >
                <window.I.X size={11} /> Reset filters
              </button>
            )}
          </div>
        )}

        <div className="mobile-feed-meta">
          <span>
            Showing <strong>
              {window.countOpportunities ? window.countOpportunities(filtered) : filtered.length}
            </strong> of {opps.length}
            {sort && sort !== "best" && <span> · sort: {sortLabels[sort]}</span>}
          </span>
        </div>

        {showFeedSkeleton ? (
          <div className="mobile-empty">
            <div className="mobile-empty-title">Loading opportunities…</div>
          </div>
        ) : filtered.length === 0 ? (
          <div className="mobile-empty">
            <div className="mobile-empty-title">
              {opps.length > 0 ? "Nothing matches these filters" : "No opportunities yet"}
            </div>
            <div className="mobile-empty-desc">
              {opps.length > 0
                ? "Try lowering the min match or clearing the search."
                : "Run a scan from the Scans tab to surface fresh threads."}
            </div>
          </div>
        ) : (
          <div className="mobile-brief-list">
            {filtered.map((o, i) => (
              <MobileBriefRow
                key={o.id}
                opp={o}
                index={i}
                onOpen={onTap}
              />
            ))}
          </div>
        )}
      </div>
    </MobileChrome>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Mobile Detail
// ────────────────────────────────────────────────────────────────────────────

function mobilePermalinkUrl(raw) {
  if (!raw) return null;
  return raw.startsWith("http")
    ? raw
    : `https://www.reddit.com${raw.startsWith("/") ? "" : "/"}${raw}`;
}

// The author a PM should target — null when the row has no resolvable author
// (deleted account, cluster, etc.), in which case we hide the "Generate PM" CTA.
function mobileRecipientFor(opp) {
  const name = String(opp?.authorName || "").trim().replace(/^\/?u\//i, "").trim();
  return name || null;
}

function MobileFeedbackButtons({ opp, onFeedback }) {
  return (
    <div className="mobile-detail-fb">
      {[
        { id: "relevant", label: "Good match", icon: "ThumbsUp" },
        { id: "not_relevant", label: "Not relevant", icon: "ThumbsDown" },
        { id: "wrong_customer", label: "Wrong customer", icon: "User" },
        { id: "too_broad", label: "Too broad", icon: "Layers" },
        { id: "already_seen", label: "Already seen", icon: "Eye" },
      ].map((r) => {
        const Cmp = window.I[r.icon];
        const isPositive = r.id === "relevant";
        const active = opp.feedback === r.id;
        return (
          <button
            key={r.id}
            className={
              "feedback-btn mobile-feedback-btn " +
              (active ? (isPositive ? "is-active positive" : "is-active negative") : "")
            }
            onClick={() => onFeedback?.(opp.id, active ? null : r.id)}
          >
            <Cmp size={13} />
            {r.label}
          </button>
        );
      })}
    </div>
  );
}

function MobileDeleteOpportunityButton({ opportunityId, onDeleteOpportunity, style }) {
  if (!opportunityId) return null;
  return (
    <button
      className="btn btn-ghost mobile-cta mobile-danger-link"
      type="button"
      style={style}
      onClick={() => {
        if (!onDeleteOpportunity) return;
        if (!window.confirm("Delete this opportunity from the inbox? This also removes its saved feedback history for this profile.")) {
          return;
        }
        onDeleteOpportunity(opportunityId);
      }}
    >
      <window.I.Trash size={13} />
      Delete
    </button>
  );
}

function MobileClusterDetail({ opp, onFeedback, onDeleteOpportunity }) {
  const members = Array.isArray(opp.members) ? opp.members : [];
  const threadTitle = opp.threadTitle || opp.title;
  const threadUrl = (() => {
    const url = mobilePermalinkUrl(opp.permalink);
    if (!url) return null;
    const m = url.match(/^(.*?\/comments\/[a-z0-9]+\/[^/]+\/)/i);
    return m ? m[1] : url;
  })();

  return (
    <div className="mobile-detail-body">
      <div className="mobile-detail-meta">
        <span className="mobile-card-sub">{opp.subreddit}</span>
        <span className="opp-meta-sep" />
        <span className="mobile-card-time">{opp.posted}</span>
      </div>
      <h3 className="mobile-detail-h">Comments on: {threadTitle}</h3>
      <div className="mobile-detail-why" style={{ marginTop: 0 }}>
        {members.length} commenters in this thread match your profile. Review each one and react individually.
      </div>
      {threadUrl && (
        <a
          href={threadUrl}
          target="_blank"
          rel="noopener noreferrer"
          style={{ color: "var(--accent)", textDecoration: "none", fontSize: 13, fontWeight: 500 }}
        >
          Open the full thread
        </a>
      )}

      {members.map((member, idx) => {
        const commentUrl = mobilePermalinkUrl(member.permalink);
        return (
          <div
            key={member.id}
            style={{
              marginTop: 18,
              paddingTop: 18,
              borderTop: "1px solid var(--border-subtle)",
            }}
          >
            <div className="mobile-detail-meta" style={{ marginBottom: 8 }}>
              <span className="pill pill-neutral">Comment {idx + 1} of {members.length}</span>
              <span className="opp-meta-sep" />
              <span className="mobile-card-time">{member.score}% match</span>
              {member.isNew && <span className="opp-new-dot" />}
            </div>
            <h3 className="mobile-detail-h" style={{ fontSize: 18, marginBottom: 10 }}>{member.title}</h3>
            {member.excerpt && (
              <div className="mobile-detail-excerpt">{member.excerpt}</div>
            )}

            <div className="mobile-detail-label">Why this matched</div>
            <div className="mobile-detail-why">{member.why}</div>

            {member.angle && (
              <div className="mobile-callout mobile-callout-accent">
                <div className="mobile-callout-title">Suggested angle</div>
                {member.angle}
              </div>
            )}

            <div className="mobile-callout mobile-callout-warn">
              <div className="mobile-callout-title">
                <window.I.Info size={13} /> Before you reply
              </div>
              {member.caution || "Read the thread, check subreddit rules, disclose your affiliation."}
            </div>

            <div className="mobile-detail-label">Feedback</div>
            <MobileFeedbackButtons opp={member} onFeedback={onFeedback} />
            <div className="mobile-detail-fb-note">
              Your feedback tunes future scans for this profile.
            </div>
            <MobileDeleteOpportunityButton
              opportunityId={member.opportunityId || null}
              onDeleteOpportunity={onDeleteOpportunity}
              style={{ marginTop: 10 }}
            />

            {commentUrl && (
              <a
                className="btn btn-secondary mobile-cta"
                href={commentUrl}
                target="_blank"
                rel="noopener noreferrer"
                style={{ marginTop: 10 }}
              >
                <window.I.External size={14} />
                Open comment on Reddit
              </a>
            )}
          </div>
        );
      })}
    </div>
  );
}

function MobileDetail({ opp, onBack, onFeedback, onDeleteOpportunity, activeProfileId, onNavigate }) {
  // genMode: null = closed, "comment" = public reply, "pm" = private message.
  const [genMode, setGenMode] = React.useState(null);
  // Close the Generate dialog when the viewed opportunity changes.
  React.useEffect(() => { setGenMode(null); }, [opp?.id]);
  if (!opp) return null;
  const url = mobilePermalinkUrl(opp.permalink || "");
  const deleteId = opp.opportunityId || null;
  const headline = opp.title;
  const rawRedditTitle = opp.redditTitle || null;
  const showRawTitle =
    !!rawRedditTitle && rawRedditTitle.trim() !== headline.trim();
  const isCluster = opp.kind === "comment-cluster";
  const recipient = mobileRecipientFor(opp);

  return (
    <div className="mobile-chrome">
      <div className="mobile-detail-header">
        <button
          className="btn btn-ghost btn-icon"
          style={{ width: 30, height: 30 }}
          onClick={onBack}
          aria-label="Back"
        >
          <window.I.ChevronLeft size={16} />
        </button>
        <div className="mobile-detail-title">Opportunity</div>
        <span className={"pill " + (opp.tier === "high" ? "pill-success" : "pill-accent")}>
          <span className="mono">{opp.score}%</span> {opp.tier === "high" ? "High" : opp.tier === "medium" ? "Med" : "Low"}
        </span>
      </div>

      <div className="mobile-chrome-scroll">
        {isCluster ? (
          <MobileClusterDetail
            opp={opp}
            onFeedback={onFeedback}
            onDeleteOpportunity={onDeleteOpportunity}
          />
        ) : (
          <div className="mobile-detail-body">
            <div className="mobile-detail-meta">
              <span className="mobile-card-sub">{opp.subreddit}</span>
              <span className="opp-meta-sep" />
              <span className="mobile-card-time">{opp.posted} · {opp.comments} comments</span>
            </div>
            <h3 className="mobile-detail-h">{headline}</h3>
            {showRawTitle && (
              <div className="mobile-detail-reddit-thread">
                Reddit thread: <span>{rawRedditTitle}</span>
              </div>
            )}

            {opp.excerpt && (
              <div className="mobile-detail-excerpt">{opp.excerpt}</div>
            )}

            <div className="mobile-detail-label">Why this matched</div>
            <div className="mobile-detail-why">{opp.why}</div>

            {opp.angle && (
              <div className="mobile-callout mobile-callout-accent">
                <div className="mobile-callout-title">Suggested angle</div>
                {opp.angle}
              </div>
            )}

            <div className="mobile-callout mobile-callout-warn">
              <div className="mobile-callout-title">
                <window.I.Info size={13} /> Before you reply
              </div>
              Read the thread, check subreddit rules, disclose your affiliation.
            </div>

            <div className="mobile-detail-label">Feedback</div>
            <MobileFeedbackButtons opp={opp} onFeedback={onFeedback} />
            <div className="mobile-detail-fb-note">
              Your feedback tunes future scans for this profile.
            </div>
            <MobileDeleteOpportunityButton
              opportunityId={deleteId}
              onDeleteOpportunity={onDeleteOpportunity}
              style={{ marginTop: 10 }}
            />
          </div>
        )}
      </div>

      <div className="mobile-detail-footer">
        {!isCluster && (
          <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
            <button
              className="btn btn-secondary mobile-cta mobile-detail-footer-cta"
              type="button"
              onClick={() => setGenMode("comment")}
              style={{ flex: 1, marginBottom: 0 }}
              title="Draft a public Reddit reply for this opportunity"
            >
              <window.I.Sparkles size={14} />
              Generate Comment
            </button>
            {recipient && (
              <button
                className="btn btn-secondary mobile-cta mobile-detail-footer-cta"
                type="button"
                onClick={() => setGenMode("pm")}
                style={{ flex: 1, marginBottom: 0 }}
                title={`Draft a private message to u/${recipient}`}
              >
                <window.I.Mail size={14} />
                Generate PM
              </button>
            )}
          </div>
        )}
        <a
          className="btn btn-primary mobile-cta mobile-detail-footer-cta"
          href={url || "#"}
          target={url ? "_blank" : undefined}
          rel={url ? "noopener noreferrer" : undefined}
          onClick={(e) => { if (!url) e.preventDefault(); }}
          aria-disabled={!url}
          style={!url ? { opacity: 0.5, pointerEvents: "none" } : undefined}
        >
          <window.I.External size={14} />
          {isCluster ? "Open full thread on Reddit" : "Open on Reddit"}
        </a>
      </div>

      {genMode && window.GenerateResponseDialog && (
        <window.GenerateResponseDialog
          mode={genMode}
          opp={opp}
          profileId={activeProfileId || window.api?.storage?.readProfileId?.() || null}
          onNavigate={onNavigate}
          onClose={() => setGenMode(null)}
        />
      )}
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Mobile Scans (list + detail)
// ────────────────────────────────────────────────────────────────────────────

function formatScanWhen(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const now = new Date();
  const sameDay = d.toDateString() === now.toDateString();
  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  const isYesterday = d.toDateString() === yesterday.toDateString();
  const time = d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
  if (sameDay) return `Today, ${time}`;
  if (isYesterday) return `Yesterday, ${time}`;
  const month = d.toLocaleDateString([], { month: "short", day: "numeric" });
  return `${month}, ${time}`;
}

function MobileScans({ onScanNow, isScanning, profile, scanRefreshKey, onOpenOpp, demoScans, demoScanResultsById }) {
  const [scans, setScans] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);
  const [selectedId, setSelectedId] = React.useState(null);

  const profileId = window.api?.storage?.readProfileId?.();

  const refresh = React.useCallback(async () => {
    if (demoScans) {
      setScans(demoScans);
      setLoading(false);
      setError(null);
      return;
    }
    if (!window.api?.scan?.list) return;
    setLoading(true);
    setError(null);
    try {
      const data = await window.api.scan.list(profileId);
      setScans(Array.isArray(data?.scans) ? data.scans : []);
    } catch (e) {
      setError(e?.message || "Could not load scans");
      setScans([]);
    } finally {
      setLoading(false);
    }
  }, [demoScans, profileId]);

  React.useEffect(() => { refresh(); }, [refresh, scanRefreshKey]);

  const selected = selectedId ? scans.find((s) => s.id === selectedId) : null;

  if (selected) {
    return (
      <MobileScanDetail
        scan={selected}
        prev={scans[scans.findIndex((s) => s.id === selectedId) + 1] || null}
        onBack={() => setSelectedId(null)}
        onRerun={onScanNow}
        isScanning={isScanning}
        onOpenOpp={onOpenOpp}
        demoScanResults={demoScanResultsById?.[selected.id] || null}
      />
    );
  }

  return (
    <MobileChrome
      title="Scans"
      subtitle={`Last scan · ${profile?.lastScan || "—"}`}
    >
      <div className="mobile-page">
        <button
          className="btn btn-primary mobile-cta mobile-cta-tight"
          onClick={onScanNow}
          disabled={isScanning}
        >
          {isScanning ? (
            <>
              <window.I.Sparkles size={14} style={{ animation: "spin 900ms linear infinite" }} />
              Scanning…
            </>
          ) : (
            <>
              <window.I.Sparkles size={14} />
              Run scan now
            </>
          )}
        </button>

        <div className="mobile-info-card" style={{ marginTop: 14 }}>
        A scan runs automatically everyday. You can run one manually anytime.
        </div>

        <div className="mobile-section-head" style={{ marginTop: 22 }}>
          <div className="mobile-section-label">History</div>
          <button className="mobile-section-link" onClick={refresh} disabled={loading}>
            <window.I.Refresh size={11} style={loading ? { animation: "spin 900ms linear infinite" } : null} />
            Refresh
          </button>
        </div>

        {loading && scans.length === 0 ? (
          <div className="mobile-empty-inline">Loading scans…</div>
        ) : error ? (
          <div className="mobile-empty-inline" style={{ color: "var(--warning-text)" }}>
            <window.I.Alert size={11} style={{ verticalAlign: "-2px", marginRight: 4 }} />
            {error}
          </div>
        ) : scans.length === 0 ? (
          <div className="mobile-empty-inline">
            No scans yet. Tap <strong>Run scan now</strong> to kick one off.
          </div>
        ) : (
          <div className="mobile-card-list">
            {scans.map((s) => {
              const isRunning = s.status === "running";
              const isWaitingForFirstWave = isPreResultRunning(s);
              const isFailed = s.status === "failed";
              const isPartial = s.status === "partial";
              const themes = Array.isArray(s.themes) ? s.themes : [];
              return (
                <button
                  type="button"
                  key={s.id}
                  className="mobile-scan-row"
                  onClick={() => setSelectedId(s.id)}
                >
                  <div className="mobile-scan-row-head">
                    <span className="mobile-scan-row-when">{formatScanWhen(s.started_at)}</span>
                    <div style={{ flex: 1 }} />
                    {isRunning ? (
                      <span className="pill pill-neutral">
                        <span className="pill-dot" style={{ background: "var(--text-secondary)" }} />
                        Running
                      </span>
                    ) : isFailed ? (
                      <span className="pill pill-warning">
                        <span className="pill-dot" style={{ background: "var(--danger)" }} />
                        Failed
                      </span>
                    ) : isPartial ? (
                      <span className="pill pill-warning">
                        <span className="pill-dot" style={{ background: "var(--warning)" }} />
                        Partial
                      </span>
                    ) : (
                      <span className="pill pill-success">
                        <span className="pill-dot" style={{ background: "var(--success)" }} />
                        Complete
                      </span>
                    )}
                  </div>
                  <div className="mobile-scan-row-counts">
                    {isWaitingForFirstWave ? (
                      <span className="scan-live-label" aria-label="Scanning">
                        <span className="scan-live-label-dot" />
                        scanning
                      </span>
                    ) : (
                      <>
                        <span><strong>{s.candidates_count ?? 0}</strong> candidates</span>
                        <span className="opp-meta-sep" />
                        <span><strong>{s.surfaced_count ?? 0}</strong> surfaced</span>
                      </>
                    )}
                  </div>
                 
                  {s.error_message && (
                    <div className="mobile-scan-row-err">
                      <window.I.Alert size={10} /> {s.error_message}
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        )}
      </div>
    </MobileChrome>
  );
}

function MobileScanDetail({ scan, prev, onBack, onRerun, isScanning, onOpenOpp, demoScanResults }) {
  const [surfaced, setSurfaced] = React.useState([]);
  const [surfacedLoading, setSurfacedLoading] = React.useState(false);
  const [surfacedError, setSurfacedError] = React.useState(null);

  // Filtered-out candidates: lazily loaded when "Filtered" is tapped.
  const [filteredOpen, setFilteredOpen] = React.useState(false);
  const [filteredLoading, setFilteredLoading] = React.useState(false);
  const [filteredError, setFilteredError] = React.useState(null);
  const [filteredRows, setFilteredRows] = React.useState([]);

  React.useEffect(() => {
    let cancelled = false;
    if (demoScanResults) {
      setSurfaced(demoScanResults);
      setSurfacedError(null);
      setSurfacedLoading(false);
      return () => { cancelled = true; };
    }
    if (scan.status !== "complete" || !window.api?.opportunities?.forScan) {
      setSurfaced([]);
      return;
    }
    setSurfacedLoading(true);
    setSurfacedError(null);
    window.api.opportunities.forScan(scan.id, { onlySurfaced: true, limit: 400 })
      .then((data) => {
        if (cancelled) return;
        const rows = Array.isArray(data?.results) ? data.results : [];
        const mapped = window.api.opportunities.mapResults
          ? window.api.opportunities.mapResults(rows)
          : rows.map(window.api.opportunities.mapResult).filter(Boolean);
        setSurfaced(mapped);
      })
      .catch((e) => {
        if (cancelled) return;
        setSurfacedError(e?.message || "Could not load surfaced opportunities");
        setSurfaced([]);
      })
      .finally(() => {
        if (!cancelled) setSurfacedLoading(false);
      });
    return () => { cancelled = true; };
  }, [demoScanResults, scan.id, scan.status]);

  const candidates = scan.candidates_count ?? 0;
  const surfacedCount = scan.surfaced_count ?? 0;
  const filteredTotal = Math.max(0, candidates - surfacedCount);
  const surfacePct = candidates ? (surfacedCount / candidates) * 100 : 0;
  const candidatesDelta = prev ? candidates - (prev.candidates_count ?? 0) : null;
  const surfacedDelta = prev ? surfacedCount - (prev.surfaced_count ?? 0) : null;
  const themes = Array.isArray(scan.themes) ? scan.themes : [];

  const isRunning = scan.status === "running";
  const isWaitingForFirstWave = isPreResultRunning(scan);
  const isFailed = scan.status === "failed";
  const isPartial = scan.status === "partial";

  const openFiltered = React.useCallback(() => {
    setFilteredOpen(true);
    if (!window.api?.opportunities?.filteredForScan) {
      setFilteredRows([]);
      return;
    }
    setFilteredLoading(true);
    setFilteredError(null);
    window.api.opportunities.filteredForScan(scan.id, { limit: 800 })
      .then((data) => {
        const rows = Array.isArray(data?.results) ? data.results : [];
        setFilteredRows(window.api.opportunities.mapFiltered(rows));
      })
      .catch((e) => {
        setFilteredError(e?.message || "Could not load filtered candidates");
        setFilteredRows([]);
      })
      .finally(() => setFilteredLoading(false));
  }, [scan.id]);

  return (
    <div className="mobile-chrome">
      <div className="mobile-detail-header">
        <button
          className="btn btn-ghost btn-icon"
          style={{ width: 30, height: 30 }}
          onClick={onBack}
          aria-label="Back"
        >
          <window.I.ChevronLeft size={16} />
        </button>
        <div className="mobile-detail-title">Scan detail</div>
        {isRunning ? (
          <span className="pill pill-neutral">
            <span className="pill-dot" style={{ background: "var(--text-secondary)" }} />
            Running
          </span>
        ) : isFailed ? (
          <span className="pill pill-warning">
            <span className="pill-dot" style={{ background: "var(--danger)" }} />
            Failed
          </span>
        ) : isPartial ? (
          <span className="pill pill-warning">
            <span className="pill-dot" style={{ background: "var(--warning)" }} />
            Partial
          </span>
        ) : (
          <span className="pill pill-success">
            <span className="pill-dot" style={{ background: "var(--success)" }} />
            Done
          </span>
        )}
      </div>

      <div className="mobile-chrome-scroll">
        <div className="mobile-page">
          <div className="mobile-hero" style={{ marginBottom: 16 }}>
            <div className="mobile-hero-eyebrow">{formatScanWhen(scan.started_at)}</div>
            <div className="mobile-hero-title" style={{ fontSize: 18 }}>
              {isWaitingForFirstWave ? "Scanning" : `${surfacedCount} surfaced`}
            </div>
            <div className="mobile-hero-sub">
              {isWaitingForFirstWave
                ? "Watching for the first wave of matches to come in."
                : `${candidates.toLocaleString()} candidate${candidates === 1 ? "" : "s"} checked · ${surfacePct.toFixed(1)}% surface rate`}
            </div>
          </div>

          {!isWaitingForFirstWave && (
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8, marginBottom: 16 }}>
              <MobileFunnelStat label="Candidates" value={candidates} delta={candidatesDelta} />
              <MobileFunnelStat
                label="Filtered"
                value={filteredTotal}
                bordered
                onClick={filteredTotal > 0 ? openFiltered : undefined}
              />
              <MobileFunnelStat label="Surfaced" value={surfacedCount} delta={surfacedDelta} accent />
            </div>
          )}

          {scan.error_message && (
            <div className="mobile-callout mobile-callout-warn" style={{ marginBottom: 14 }}>
              <div className="mobile-callout-title">
                <window.I.Alert size={13} /> {isFailed ? "Scan failed" : "Partial scan"}
              </div>
              {scan.error_message}
            </div>
          )}

          <div className="mobile-section-label" style={{ marginTop: 20 }}>
            Surfaced opportunities · {surfacedCount}
          </div>

          {surfacedLoading ? (
            <div className="mobile-empty-inline">Loading…</div>
          ) : surfacedError ? (
            <div className="mobile-empty-inline" style={{ color: "var(--warning-text)" }}>
              <window.I.Alert size={11} style={{ verticalAlign: "-2px", marginRight: 4 }} />
              {surfacedError}
            </div>
          ) : surfaced.length === 0 ? (
            <div className="mobile-empty-inline">
              {isRunning
                ? "Scan still running — refresh once it finishes."
                : isFailed
                ? "This scan didn't complete, so no opportunities were stored."
                : "Nothing surfaced this run."}
            </div>
          ) : (
            <div className="mobile-brief-list">
              {surfaced.map((o, i) => (
                <MobileBriefRow
                  key={o.id}
                  opp={o}
                  index={i}
                  onOpen={onOpenOpp}
                />
              ))}
            </div>
          )}

          <button
            className="btn btn-secondary mobile-cta"
            onClick={onRerun}
            disabled={isScanning}
            style={{ marginTop: 20 }}
          >
            <window.I.Refresh
              size={14}
              style={isScanning ? { animation: "spin 900ms linear infinite" } : null}
            />
            {isScanning ? "Scanning…" : "Re-run scan"}
          </button>
        </div>
      </div>

      {filteredOpen && (
        <MobileFilteredSheet
          total={filteredTotal}
          rows={filteredRows}
          loading={filteredLoading}
          error={filteredError}
          onClose={() => setFilteredOpen(false)}
        />
      )}
    </div>
  );
}

function MobileFunnelStat({ label, value, delta, accent, bordered, onClick }) {
  const clickable = typeof onClick === "function";
  const deltaText = delta == null ? null : delta === 0 ? "—" : `${delta > 0 ? "▲" : "▼"} ${Math.abs(delta)}`;
  const deltaColor = delta == null || delta === 0 ? "var(--text-tertiary)" : delta > 0 ? "var(--success)" : "var(--danger)";
  return (
    <div
      role={clickable ? "button" : undefined}
      onClick={clickable ? onClick : undefined}
      className={"mobile-stat" + (accent ? " mobile-stat-accent" : "")}
      style={{
        cursor: clickable ? "pointer" : "default",
        ...(bordered ? { borderColor: "var(--accent-border, var(--border))" } : null),
      }}
    >
      <div className="mobile-stat-label" style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 4 }}>
        {label}
        {clickable && <window.I.ChevronRight size={13} style={{ color: "var(--text-tertiary)" }} />}
      </div>
      <div className="mobile-stat-value" style={accent ? { color: "var(--accent-soft-text, var(--accent))" } : null}>
        {value.toLocaleString()}
      </div>
      {deltaText ? (
        <div className="mobile-stat-meta" style={{ color: deltaColor, fontSize: 11, marginTop: 4 }}>{deltaText}</div>
      ) : clickable && value > 0 ? (
        <div className="mobile-stat-meta" style={{ color: "var(--accent-soft-text, var(--accent))", fontSize: 10.5, marginTop: 4 }}>Tap to view</div>
      ) : null}
    </div>
  );
}

const MOBILE_FILTERED_KIND_FILTERS = [
  { id: "all", label: "All" },
  { id: "post", label: "Posts" },
  { id: "comment", label: "Comments" },
];

function MobileFilteredSheet({ total, rows, loading, error, onClose }) {
  const [kind, setKind] = React.useState("all");
  const [subreddit, setSubreddit] = React.useState("all");
  const [detail, setDetail] = React.useState(null);
  const [genMode, setGenMode] = React.useState(null);

  const subreddits = React.useMemo(
    () => Array.from(new Set(rows.map((r) => r.subreddit).filter(Boolean))).sort((a, b) => a.localeCompare(b)),
    [rows]
  );
  const visible = React.useMemo(
    () => rows.filter((r) => {
      const okKind = kind === "all" || r.kind === kind;
      const okSub = subreddit === "all" || r.subreddit === subreddit;
      return okKind && okSub;
    }),
    [rows, kind, subreddit]
  );

  const detailOpp = detail && window.api?.opportunities?.filteredToOpp
    ? window.api.opportunities.filteredToOpp(detail)
    : null;
  const recipient = detail
    ? String(detail.authorName || "").trim().replace(/^\/?u\//i, "").trim() || null
    : null;
  const detailUrl = detail ? mobilePermalinkUrl(detail.permalink) : null;
  const profileId = window.api?.storage?.readProfileId?.() || null;

  return (
    <div className="mobile-chrome" style={{ position: "fixed", inset: 0, zIndex: 120, background: "var(--bg)" }}>
      <div className="mobile-detail-header">
        <button className="btn btn-ghost btn-icon" style={{ width: 30, height: 30 }} onClick={onClose} aria-label="Close">
          <window.I.X size={16} />
        </button>
        <div className="mobile-detail-title">Filtered out · {total.toLocaleString()}</div>
        <span style={{ width: 30 }} />
      </div>

      <div className="mobile-chrome-scroll">
        <div className="mobile-page">
          <div className="mobile-info-card" style={{ marginBottom: 14 }}>
            Candidates this scan checked but didn&apos;t surface. Most never reached the deeper relevance pass.
          </div>

          {!loading && !error && rows.length > 0 && (
            <>
              <div className="scan-segmented" role="tablist" aria-label="Filter by type" style={{ marginBottom: 10 }}>
                {MOBILE_FILTERED_KIND_FILTERS.map((opt) => (
                  <button
                    key={opt.id}
                    type="button"
                    className={"scan-segmented-opt " + (kind === opt.id ? "is-on" : "")}
                    onClick={() => setKind(opt.id)}
                    aria-pressed={kind === opt.id ? "true" : "false"}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
              {subreddits.length > 1 && (
                <select
                  value={subreddit}
                  onChange={(e) => setSubreddit(e.target.value)}
                  style={{
                    width: "100%", fontSize: 13, padding: "9px 10px", marginBottom: 14,
                    borderRadius: "var(--r-md)", border: "1px solid var(--border)",
                    background: "var(--bg-card)", color: "var(--text-primary)",
                  }}
                >
                  <option value="all">All subreddits</option>
                  {subreddits.map((s) => (<option key={s} value={s}>{s}</option>))}
                </select>
              )}
            </>
          )}

          {loading ? (
            <div className="mobile-empty-inline">Loading filtered candidates…</div>
          ) : error ? (
            <div className="mobile-empty-inline" style={{ color: "var(--warning-text)" }}>
              <window.I.Alert size={11} style={{ verticalAlign: "-2px", marginRight: 4 }} />
              {error}
            </div>
          ) : rows.length === 0 ? (
            <div className="mobile-empty-inline">No filtered candidates were stored for this scan.</div>
          ) : visible.length === 0 ? (
            <div className="mobile-empty-inline">No candidates match the current filter.</div>
          ) : (
            <div className="mobile-card-list">
              {visible.map((row) => (
                <MobileFilteredCard key={row.id} row={row} onOpen={() => setDetail(row)} />
              ))}
            </div>
          )}
        </div>
      </div>

      {detail && (
        <div className="mobile-chrome" style={{ position: "fixed", inset: 0, zIndex: 130, background: "var(--bg)" }}>
          <div className="mobile-detail-header">
            <button className="btn btn-ghost btn-icon" style={{ width: 30, height: 30 }} onClick={() => setDetail(null)} aria-label="Back">
              <window.I.ChevronLeft size={16} />
            </button>
            <div className="mobile-detail-title">Candidate</div>
            <span className="pill pill-accent"><span className="mono">{detail.score}%</span></span>
          </div>
          <div className="mobile-chrome-scroll">
            <div className="mobile-detail-body">
              <div className="mobile-detail-meta">
                <span className="mobile-card-sub">{detail.subreddit}</span>
                <span className="opp-meta-sep" />
                <span className="mobile-card-time">{detail.posted} · {detail.comments} comments</span>
              </div>
              <h3 className="mobile-detail-h">{detail.title}</h3>
              {detail.body && <div className="mobile-detail-excerpt">{detail.body}</div>}
            </div>
          </div>
          <div className="mobile-detail-footer">
            <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
              <button
                className="btn btn-secondary mobile-cta mobile-detail-footer-cta"
                type="button"
                onClick={() => setGenMode("comment")}
                style={{ flex: 1, marginBottom: 0 }}
              >
                <window.I.Sparkles size={14} />
                Generate Comment
              </button>
              {recipient && (
                <button
                  className="btn btn-secondary mobile-cta mobile-detail-footer-cta"
                  type="button"
                  onClick={() => setGenMode("pm")}
                  style={{ flex: 1, marginBottom: 0 }}
                >
                  <window.I.Mail size={14} />
                  Generate PM
                </button>
              )}
            </div>
            <a
              className="btn btn-primary mobile-cta mobile-detail-footer-cta"
              href={detailUrl || "#"}
              target={detailUrl ? "_blank" : undefined}
              rel={detailUrl ? "noopener noreferrer" : undefined}
              onClick={(e) => { if (!detailUrl) e.preventDefault(); }}
              aria-disabled={!detailUrl}
              style={!detailUrl ? { opacity: 0.5, pointerEvents: "none" } : undefined}
            >
              <window.I.External size={14} />
              Open on Reddit
            </a>
          </div>
        </div>
      )}

      {genMode && detailOpp && window.GenerateResponseDialog && (
        <window.GenerateResponseDialog
          mode={genMode}
          opp={detailOpp}
          profileId={profileId}
          onClose={() => setGenMode(null)}
        />
      )}
    </div>
  );
}

function MobileFilteredCard({ row, onOpen }) {
  const tone = row.tier === "high" ? "success" : row.tier === "medium" ? "accent" : "neutral";
  return (
    <button type="button" className="mobile-scan-row" onClick={onOpen} style={{ alignItems: "stretch" }}>
      <div className="mobile-detail-meta" style={{ marginBottom: 6 }}>
        <span className="pill pill-neutral" style={{ fontSize: 10, textTransform: "uppercase" }}>{row.kind}</span>
        <span className="mobile-card-sub">{row.subreddit}</span>
        <span className="opp-meta-sep" />
        <span className="mobile-card-time">{row.posted}</span>
        <div style={{ flex: 1 }} />
        <MobilePill tone={tone}>{row.score}%</MobilePill>
      </div>
      <div className="mobile-card-title" style={{ fontSize: 14, fontWeight: 500, color: "var(--text-primary)", lineHeight: 1.4, textAlign: "left" }}>
        {row.title}
      </div>
      {row.excerpt && (
        <div style={{
          fontSize: 12.5, color: "var(--text-secondary)", lineHeight: 1.45, marginTop: 4, textAlign: "left",
          display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden",
        }}>
          {row.excerpt}
        </div>
      )}
    </button>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Mobile Settings (with embedded Notifications, Profile, Data, Account)
// ────────────────────────────────────────────────────────────────────────────

function MobileNotificationsSection({ onNavigate } = {}) {
  const normalizeDigestTime = React.useCallback(
    (value) => (/^\d{2}:\d{2}$/.test(value || "") ? value : "09:00"),
    []
  );
  const getLocalTimeZone = React.useCallback(() => {
    try {
      return Intl.DateTimeFormat().resolvedOptions().timeZone || null;
    } catch (e) {
      return null;
    }
  }, []);
  const getUtcDigestTime = React.useCallback((localTime) => {
    const normalized = normalizeDigestTime(localTime);
    const [hours, minutes] = normalized.split(":").map(Number);
    const now = new Date();
    const localDate = new Date(
      now.getFullYear(),
      now.getMonth(),
      now.getDate(),
      hours,
      minutes,
      0,
      0
    );
    return `${String(localDate.getUTCHours()).padStart(2, "0")}:${String(localDate.getUTCMinutes()).padStart(2, "0")}`;
  }, [normalizeDigestTime]);
  const planState = (window.usePlan ? window.usePlan() : null) || {
    loading: true,
    plan: "free",
    limits: { daily_digest_enabled: false },
  };
  const digestLocked = !planState?.loading && !planState?.limits?.daily_digest_enabled;
  const goPricing = React.useCallback(() => {
    if (typeof onNavigate === "function") onNavigate("pricing");
    else {
      try {
        window.history.pushState({}, "", "/pricing");
        window.dispatchEvent(new PopStateEvent("popstate"));
      } catch (e) {
        window.location.assign("/pricing");
      }
    }
  }, [onNavigate]);
  const userId = window.api?.auth?.readUserId?.() || null;
  const { user } = window.useCurrentUser();
  const storedEmail = React.useMemo(() => {
    try {
      const auth = JSON.parse(localStorage.getItem("cn_auth_v1") || "null");
      return auth?.email || "";
    } catch (e) {
      return "";
    }
  }, []);
  const recipientEmail = user?.email || storedEmail || "";

  const [st, setSt] = React.useState({
    digest: false,
    digestTime: "09:00",
    emailTheme: "light",
  });
  const localTimeZone = React.useMemo(() => getLocalTimeZone(), [getLocalTimeZone]);
  const digestUtcTime = React.useMemo(
    () => getUtcDigestTime(st.digestTime),
    [getUtcDigestTime, st.digestTime]
  );
  const [loaded, setLoaded] = React.useState(false);
  const [hasSavedPreferences, setHasSavedPreferences] = React.useState(false);
  const skipNextSaveRef = React.useRef(true);
  const saveTimerRef = React.useRef(null);
  const [saveStatus, setSaveStatus] = React.useState("idle");

  React.useEffect(() => {
    if (!userId) {
      setLoaded(true);
      setHasSavedPreferences(false);
      skipNextSaveRef.current = false;
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const resp = await window.api.preferences.get(userId);
        const p = resp?.preferences;
        if (!cancelled) {
          setHasSavedPreferences(!!p);
        }
        if (!cancelled && p) {
          setSt((s) => ({
            ...s,
            digest: typeof p.digest_enabled === "boolean" ? p.digest_enabled : s.digest,
            digestTime: normalizeDigestTime(p.digest_local_time || p.digest_time || s.digestTime),
            emailTheme: p.theme === "dark" ? "dark" : p.theme === "light" ? "light" : s.emailTheme,
          }));
        }
      } catch (e) {
        console.warn("Could not load notification preferences:", e);
      } finally {
        if (!cancelled) {
          setLoaded(true);
          requestAnimationFrame(() => {
            skipNextSaveRef.current = false;
          });
        }
      }
    })();
    return () => { cancelled = true; };
  }, [userId]);

  React.useEffect(() => {
    if (!loaded || !userId) return;
    if (skipNextSaveRef.current) return;
    if (!hasSavedPreferences && !st.digest) {
      setSaveStatus("idle");
      return;
    }
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    setSaveStatus("saving");
    saveTimerRef.current = setTimeout(async () => {
      try {
        await window.api.preferences.update(userId, {
          email: recipientEmail || null,
          digest_enabled: st.digest,
          digest_time: digestUtcTime,
          digest_local_time: normalizeDigestTime(st.digestTime),
          digest_timezone: localTimeZone,
          theme: st.emailTheme,
        });
        setHasSavedPreferences(true);
        setSaveStatus("saved");
        setTimeout(() => {
          setSaveStatus((s) => (s === "saved" ? "idle" : s));
        }, 1500);
      } catch (e) {
        console.warn("Autosave failed:", e);
        setSaveStatus("err");
      }
    }, 400);
    return () => {
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    };
  }, [digestUtcTime, hasSavedPreferences, st.digest, st.digestTime, st.emailTheme, recipientEmail, loaded, localTimeZone, normalizeDigestTime, userId]);

  const TIMES = React.useMemo(() => {
    const opts = [];
    for (let h = 6; h <= 22; h++) {
      for (let m = 0; m < 60; m += 30) {
        const value = `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
        const hour12 = ((h + 11) % 12) + 1;
        const ampm = h < 12 ? "AM" : "PM";
        opts.push({ value, label: `${hour12}:${String(m).padStart(2, "0")} ${ampm}` });
      }
    }
    return opts;
  }, []);

  return (
    <div>
      <div className="mobile-section-head">
        <div className="mobile-section-label">Notifications</div>
        <div className="mobile-save-status" style={{
          color:
            saveStatus === "err" ? "var(--danger-text)" :
              saveStatus === "saving" ? "var(--text-tertiary)" :
                saveStatus === "saved" ? "var(--success-text)" : "transparent",
        }}>
          {saveStatus === "saving" && "Saving…"}
          {saveStatus === "saved" && "Saved"}
          {saveStatus === "err" && "Couldn't save"}
        </div>
      </div>
      <div className="mobile-section-card">
        <div className="mobile-list-row" style={{ cursor: "default" }}>
          <div className="mobile-list-row-icon">
            <window.I.Mail size={14} />
          </div>
          <div style={{ flex: 1, textAlign: "left", minWidth: 0 }}>
            <div className="mobile-list-row-title" style={{ display: "inline-flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
              Daily digest email
              {digestLocked && (
                <span
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 3,
                    padding: "1px 7px",
                    borderRadius: 999,
                    background: "var(--accent-subtle)",
                    color: "var(--accent-soft-text)",
                    border: "1px solid var(--accent-border)",
                    fontSize: 9.5,
                    fontWeight: 700,
                    letterSpacing: 0.4,
                    textTransform: "uppercase",
                  }}
                >
                  <window.I.Lock size={9} />
                  Solo
                </span>
              )}
            </div>
            <div className="mobile-list-row-meta">
              {digestLocked ? (
                <>
                  Upgrade to Solo to receive a daily email with your top opportunities.{" "}
                  <a
                    href="#"
                    onClick={(e) => {
                      e.preventDefault();
                      goPricing();
                    }}
                    style={{ color: "var(--accent-soft-text)", fontWeight: 600 }}
                  >
                    See plans →
                  </a>
                </>
              ) : (
                recipientEmail || "No email on file"
              )}
            </div>
          </div>
          <label className="switch">
            <input
              type="checkbox"
              checked={!!st.digest && !digestLocked}
              onChange={(e) => {
                if (e.target.checked && digestLocked) {
                  goPricing();
                  return;
                }
                setSt((s) => ({ ...s, digest: e.target.checked }));
              }}
            />
            <span className="switch-slider" />
          </label>
        </div>

        {st.digest && (
          <div className="mobile-list-row mobile-list-row-stack" style={{ cursor: "default" }}>
            <div className="mobile-list-row-icon">
              <window.I.Clock size={14} />
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div className="mobile-list-row-title">Delivery time</div>
              <div className="mobile-list-row-meta">
                {localTimeZone || "Local time"} · daily
              </div>
            </div>
            <select
              className="mobile-select"
              value={st.digestTime}
              onChange={(e) => setSt((s) => ({ ...s, digestTime: e.target.value }))}
            >
              {TIMES.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>
        )}

        {st.digest && (
          <div className="mobile-list-row mobile-list-row-stack" style={{ cursor: "default" }}>
            <div className="mobile-list-row-icon">
              <window.I.Sun size={14} />
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div className="mobile-list-row-title">Email theme</div>
              <div className="mobile-list-row-meta">How the digest looks</div>
            </div>
            <select
              className="mobile-select"
              value={st.emailTheme}
              onChange={(e) => setSt((s) => ({ ...s, emailTheme: e.target.value }))}
            >
              <option value="light">Light</option>
              <option value="dark">Dark</option>
            </select>
          </div>
        )}
      </div>
    </div>
  );
}

function MobileRedditSection() {
  const userId = window.api?.auth?.readUserId?.() || null;
  const [loading, setLoading] = React.useState(true);
  const [connection, setConnection] = React.useState(null);
  const [busy, setBusy] = React.useState("");
  const [error, setError] = React.useState("");
  // Banner from the OAuth callback redirect (?reddit=connected|error).
  const [banner, setBanner] = React.useState(null);

  const refresh = React.useCallback(async () => {
    if (!userId) { setLoading(false); return; }
    try {
      const resp = await window.api.reddit.connection(userId);
      setConnection(resp?.connection || { connected: false });
    } catch (e) {
      setConnection({ connected: false });
    } finally {
      setLoading(false);
    }
  }, [userId]);

  React.useEffect(() => { refresh(); }, [refresh]);

  // Read + clear the ?reddit= flag the backend callback bounces back with.
  React.useEffect(() => {
    try {
      const url = new URL(window.location.href);
      const status = url.searchParams.get("reddit");
      if (!status) return;
      const reason = url.searchParams.get("reddit_reason");
      url.searchParams.delete("reddit");
      url.searchParams.delete("reddit_reason");
      window.history.replaceState({}, "", url.toString());
      if (status === "connected") {
        setBanner({ kind: "success", text: "Reddit account connected." });
        refresh();
      } else {
        setBanner({
          kind: "error",
          text: reason
            ? `Could not connect Reddit: ${decodeURIComponent(reason)}`
            : "Could not connect Reddit. Please try again.",
        });
      }
    } catch (e) {}
  }, [refresh]);

  const onConnect = async () => {
    if (!userId) return;
    setError("");
    setBusy("connect");
    try {
      const resp = await window.api.reddit.startOAuth(userId, "/settings");
      if (resp?.url) window.location.assign(resp.url);
      else throw new Error("Could not start the Reddit connection.");
    } catch (e) {
      setBusy("");
      setError(e?.message || "Could not start the Reddit connection.");
    }
  };

  const onDisconnect = async () => {
    if (!userId) return;
    if (!window.confirm("Disconnect your Reddit account? You'll need to reconnect before you can post replies again.")) {
      return;
    }
    setError("");
    setBusy("disconnect");
    try {
      await window.api.reddit.disconnect(userId);
      setConnection({ connected: false });
      setBanner(null);
    } catch (e) {
      setError(e?.message || "Could not disconnect Reddit.");
    } finally {
      setBusy("");
    }
  };

  if (!userId) return null;

  const connected = !!connection?.connected;
  const username = connection?.reddit_username;

  return (
    <div>
      <div className="mobile-section-label">Reddit connection</div>
      <div className="mobile-section-card">
        <div className="mobile-list-row" style={{ cursor: "default" }}>
          <div
            className="mobile-list-row-icon"
            style={connected ? { color: "var(--success-text)" } : undefined}
          >
            <window.I.Reddit size={14} />
          </div>
          <div style={{ flex: 1, textAlign: "left", minWidth: 0 }}>
            <div className="mobile-list-row-title">
              {loading
                ? "Checking connection…"
                : connected
                  ? `Connected as u/${username || "—"}`
                  : "Not connected"}
            </div>
            <div className="mobile-list-row-meta">
              {connected
                ? "We post replies under this account only when you tap Post now."
                : "Connect Reddit to post replies from your own account."}
            </div>
          </div>
          {!loading && connected && (
            <span className="pill pill-success" style={{ fontSize: 10.5 }}>
              <span className="pill-dot" style={{ background: "var(--success)" }} />
              Linked
            </span>
          )}
        </div>

        {!loading && (
          <div className="mobile-list-row" style={{ cursor: "default", gap: 8 }}>
            {connected ? (
              <>
                <button
                  type="button"
                  className="btn btn-secondary btn-sm"
                  style={{ flex: 1 }}
                  onClick={onConnect}
                  disabled={busy === "connect"}
                >
                  {busy === "connect" ? (
                    <window.I.Refresh size={12} style={{ animation: "spin 900ms linear infinite" }} />
                  ) : (
                    <window.I.Refresh size={12} />
                  )}
                  Reconnect
                </button>
                <button
                  type="button"
                  className="btn btn-secondary btn-sm"
                  style={{ flex: 1, color: "var(--danger-text)", borderColor: "var(--danger-border)" }}
                  onClick={onDisconnect}
                  disabled={busy === "disconnect"}
                >
                  {busy === "disconnect" ? "Disconnecting…" : "Disconnect"}
                </button>
              </>
            ) : (
              <button
                type="button"
                className="btn btn-primary btn-sm"
                style={{ flex: 1 }}
                onClick={onConnect}
                disabled={busy === "connect"}
              >
                {busy === "connect" ? (
                  <window.I.Refresh size={12} style={{ animation: "spin 900ms linear infinite" }} />
                ) : (
                  <window.I.Reddit size={12} />
                )}
                Connect Reddit
              </button>
            )}
          </div>
        )}
      </div>

      {banner && (
        <div
          className="mobile-list-row-meta"
          style={{
            marginTop: 8,
            padding: "8px 10px",
            borderRadius: 8,
            color: banner.kind === "success" ? "var(--success-text)" : "var(--danger-text)",
            background: banner.kind === "success" ? "var(--success-subtle)" : "var(--danger-subtle)",
            border: "1px solid",
            borderColor: banner.kind === "success" ? "var(--success-border)" : "var(--danger-border)",
          }}
        >
          {banner.text}
        </div>
      )}

      {error && (
        <div
          className="mobile-list-row-meta"
          style={{
            marginTop: 8,
            padding: "8px 10px",
            borderRadius: 8,
            color: "var(--danger-text)",
            background: "var(--danger-subtle)",
            border: "1px solid var(--danger-border)",
          }}
        >
          {error}
        </div>
      )}
    </div>
  );
}

function MobileDataSection({ activeProfileId, currentProfile, onRefreshProfiles, onSwitchProfile, onRefreshOpps, onResultsCleared }) {
  const [exporting, setExporting] = React.useState(false);
  const [clearing, setClearing] = React.useState(false);
  const [deletingProfile, setDeletingProfile] = React.useState(false);

  const onExport = async () => {
    if (exporting) return;
    const profileId = activeProfileId || window.api?.storage?.readProfileId?.();
    if (!profileId) { window.alert("No active business profile to export."); return; }
    setExporting(true);
    try {
      const data = await window.api.profile.exportResults(profileId);
      const payload = data?.export || {};
      const slug = (payload?.profile?.product_name || "profile")
        .toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "") || "profile";
      const stamp = new Date().toISOString().slice(0, 10);
      const blob = new Blob([JSON.stringify(payload, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `community-ninja-${slug}-${stamp}.json`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      window.alert("Could not export results: " + (e?.message || "unknown error"));
    } finally {
      setExporting(false);
    }
  };

  const onClear = async () => {
    if (clearing) return;
    const profileId = activeProfileId || window.api?.storage?.readProfileId?.();
    if (!profileId) { window.alert("No active business profile to clear results for."); return; }
    const ok = window.confirm("Clear all stored results for this profile? This removes opportunities, feedback, and scan history. Your profile and saved radar stay. This cannot be undone.");
    if (!ok) return;
    setClearing(true);
    try {
      await window.api.profile.clearResults(profileId);
      await onRefreshOpps?.();
      onResultsCleared?.();
    } catch (e) {
      window.alert("Could not clear results: " + (e?.message || "unknown error"));
    } finally {
      setClearing(false);
    }
  };

  const onDelete = async () => {
    if (deletingProfile) return;
    const profileId = activeProfileId || window.api?.storage?.readProfileId?.();
    if (!profileId) { window.alert("No active business profile to delete."); return; }
    const ok = window.confirm("Delete this business profile? This removes only the current profile and its related data. This cannot be undone.");
    if (!ok) return;
    setDeletingProfile(true);
    try {
      await window.api.profile.delete(profileId);
      const rows = await onRefreshProfiles?.();
      const remaining = Array.isArray(rows) ? rows : [];
      const next = remaining.find((p) => p.id !== profileId) || remaining[0] || null;
      onSwitchProfile?.(next?.id || "");
    } catch (e) {
      window.alert("Could not delete profile: " + (e?.message || "unknown error"));
    } finally {
      setDeletingProfile(false);
    }
  };

  return (
    <div>
      <div className="mobile-section-label">Data</div>
      <div className="mobile-section-card">
        <button className="mobile-list-row" onClick={onExport} disabled={exporting}>
          <div className="mobile-list-row-icon">
            <window.I.Download size={14} />
          </div>
          <div style={{ flex: 1, textAlign: "left", minWidth: 0 }}>
            <div className="mobile-list-row-title">{exporting ? "Exporting…" : "Export results"}</div>
            <div className="mobile-list-row-meta">Download as JSON</div>
          </div>
          <window.I.ChevronRight size={14} style={{ color: "var(--text-tertiary)" }} />
        </button>
        <button className="mobile-list-row" onClick={onClear} disabled={clearing || deletingProfile}>
          <div className="mobile-list-row-icon">
            <window.I.Refresh size={14} />
          </div>
          <div style={{ flex: 1, textAlign: "left", minWidth: 0 }}>
            <div className="mobile-list-row-title">{clearing ? "Clearing…" : "Clear stored results"}</div>
            <div className="mobile-list-row-meta">Keeps profile, removes scans + feedback</div>
          </div>
          <window.I.ChevronRight size={14} style={{ color: "var(--text-tertiary)" }} />
        </button>
        <button
          className="mobile-list-row mobile-list-row-danger"
          onClick={onDelete}
          disabled={deletingProfile || clearing}
        >
          <div className="mobile-list-row-icon" style={{ color: "var(--danger-text)" }}>
            <window.I.Trash size={14} />
          </div>
          <div style={{ flex: 1, textAlign: "left", minWidth: 0 }}>
            <div className="mobile-list-row-title" style={{ color: "var(--danger-text)" }}>
              {deletingProfile ? "Deleting…" : "Delete this profile"}
            </div>
            <div className="mobile-list-row-meta">
              {currentProfile?.name ? `Removes ${currentProfile.name}` : "Removes the current business profile"}
            </div>
          </div>
        </button>
      </div>
    </div>
  );
}

function MobileSettings({
  profile,
  profiles,
  userEmail,
  onLogout,
  theme,
  onToggleTheme,
  onSwitchProfile,
  onNavigate,
  activeProfileId,
  onRefreshProfiles,
  onRefreshOpps,
  onResultsCleared,
  onDeleteAccount,
  isDemo,
  onExitDemo,
}) {
  const { user } = window.useCurrentUser();
  const [loggingOut, setLoggingOut] = React.useState(false);
  const displayEmail = user?.email || userEmail || "—";
  const displayName = user?.name?.trim() || (userEmail ? userEmail.split("@")[0] : "You");
  const currentProfile = profiles?.find((p) => p.id === activeProfileId) || profile || null;
  const handleLogout = async () => {
    if (loggingOut) return;
    setLoggingOut(true);
    try {
      await onLogout?.();
    } catch (e) {
      setLoggingOut(false);
    }
  };

  return (
    <MobileChrome title="Settings" subtitle="Community Ninja">
      <div className="mobile-page">
        {/* Account card */}
        <div className="mobile-section-card mobile-account-card">
          <div className="mobile-section-card-row">
            <div className="avatar" style={{ width: 40, height: 40, fontSize: 14 }}>
              {displayName.split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase()}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div className="mobile-section-card-title">{displayName}</div>
              <div className="mobile-section-card-meta">{displayEmail}</div>
            </div>
          </div>
        </div>

        {/* Reddit connection */}
        {!isDemo && <MobileRedditSection />}

        {isDemo && onExitDemo && (
          <button type="button" className="mobile-list-row mobile-demo-exit-row" onClick={onExitDemo}>
            <div className="mobile-list-row-icon">
              <window.I.ArrowLeft size={14} />
            </div>
            <div style={{ flex: 1, textAlign: "left", minWidth: 0 }}>
              <div className="mobile-list-row-title">Exit demo</div>
              <div className="mobile-list-row-meta">Return to onboarding and create your own profile</div>
            </div>
          </button>
        )}

        {/* Active business profile */}
        <div className="mobile-section-label">Business profile</div>
        <div className="mobile-section-card">
          {profiles?.length > 0 ? (
            profiles.map((p) => (
              <button
                key={p.id}
                className="mobile-list-row"
                onClick={() => onSwitchProfile?.(p.id)}
              >
                <div
                  className="profile-mark"
                  style={!p.faviconUrl
                    ? { background: p.color, width: 28, height: 28, fontSize: 12, borderRadius: 7 }
                    : { width: 28, height: 28, borderRadius: 7 }}
                >
                  {p.faviconUrl ? <img className="profile-mark-img" src={p.faviconUrl} alt="" /> : p.initials}
                </div>
                <div className="profile-item-body" style={{ textAlign: "left" }}>
                  <div className="profile-item-name">{p.name}</div>
                  <div className="meta">{p.website}</div>
                </div>
                {p.id === activeProfileId && <window.I.Check size={14} style={{ color: "var(--accent)" }} />}
              </button>
            ))
          ) : (
            <div className="mobile-list-row" style={{ cursor: "default" }}>
              <div className="mobile-list-row-icon"><window.I.Briefcase size={14} /></div>
              <div style={{ flex: 1, textAlign: "left", minWidth: 0 }}>
                <div className="mobile-list-row-title">No profile yet</div>
                <div className="mobile-list-row-meta">Create one to start scanning</div>
              </div>
            </div>
          )}

          <button
            className="mobile-list-row"
            onClick={() => onNavigate?.("profile")}
            disabled={!currentProfile?.id}
          >
            <div className="mobile-list-row-icon">
              <window.I.Edit size={14} />
            </div>
            <div style={{ flex: 1, textAlign: "left", minWidth: 0 }}>
              <div className="mobile-list-row-title">Edit current profile</div>
              <div className="mobile-list-row-meta">Keywords, subreddits, descriptions</div>
            </div>
            <window.I.ChevronRight size={14} style={{ color: "var(--text-tertiary)" }} />
          </button>
        </div>

        {/* Appearance */}
        <div className="mobile-section-label">Appearance</div>
        <div className="mobile-section-card">
          <button className="mobile-list-row" onClick={onToggleTheme}>
            <div className="mobile-list-row-icon">
              {theme === "dark" ? <window.I.Moon size={14} /> : <window.I.Sun size={14} />}
            </div>
            <div style={{ flex: 1, textAlign: "left" }}>
              <div className="mobile-list-row-title">Theme</div>
              <div className="mobile-list-row-meta">{theme === "dark" ? "Dark" : "Light"}</div>
            </div>
            <window.I.ChevronRight size={14} style={{ color: "var(--text-tertiary)" }} />
          </button>
          <button className="mobile-list-row" onClick={() => onNavigate?.("pricing")}>
            <div className="mobile-list-row-icon">
              <window.I.Sparkles size={14} />
            </div>
            <div style={{ flex: 1, textAlign: "left" }}>
              <div className="mobile-list-row-title">Pro plans</div>
              <div className="mobile-list-row-meta">Compare Free, Solo, and Growth</div>
            </div>
            <window.I.ChevronRight size={14} style={{ color: "var(--text-tertiary)" }} />
          </button>
        </div>

        {/* Notifications */}
        <MobileNotificationsSection onNavigate={onNavigate} />

        {/* Data */}
        <MobileDataSection
          activeProfileId={activeProfileId}
          currentProfile={currentProfile}
          onRefreshProfiles={onRefreshProfiles}
          onSwitchProfile={onSwitchProfile}
          onRefreshOpps={onRefreshOpps}
          onResultsCleared={onResultsCleared}
        />

        {/* Compliance */}
        <div className="mobile-section-label">What this app does</div>
        <div className="mobile-section-card mobile-compliance-card">
          {[
            { yes: true, t: "Monitors public Reddit conversations for relevance." },
            { yes: true, t: "Links you back to Reddit to read full threads." },
            { yes: false, t: "Never posts, comments, DMs, or votes for you." },
            { yes: false, t: "Never automates replies or schedules content." },
          ].map((row, i) => (
            <div key={i} className="mobile-compliance-row">
              <div
                className={"mobile-compliance-mark " + (row.yes ? "is-yes" : "is-no")}
              >
                {row.yes ? <window.I.Check size={11} /> : <window.I.X size={11} />}
              </div>
              <div className="mobile-compliance-text">{row.t}</div>
            </div>
          ))}
        </div>

        {/* Log out / delete */}
        <button
          type="button"
          className="btn btn-secondary mobile-cta"
          onClick={handleLogout}
          disabled={loggingOut}
          style={{ marginTop: 16 }}
        >
          {loggingOut ? (
            <window.I.Refresh size={14} style={{ animation: "spin 900ms linear infinite" }} />
          ) : (
            <window.I.LogOut size={14} />
          )}
          {loggingOut ? "Logging out…" : "Log out"}
        </button>

        {onDeleteAccount && (
          <button
            type="button"
            className="btn btn-ghost mobile-cta mobile-danger-link"
            onClick={onDeleteAccount}
            style={{ marginTop: 8 }}
          >
            <window.I.Trash size={13} />
            Delete account
          </button>
        )}

        {window.LegalFooter && <window.LegalFooter variant="mobile" />}
      </div>
    </MobileChrome>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Mobile profile (edit) — reuses ProfileScreen so the form parity matches desktop.
// ────────────────────────────────────────────────────────────────────────────

function MobileProfileEdit({ profile, onUpdateProfile, onBack }) {
  return (
    <div className="mobile-chrome">
      <div className="mobile-detail-header">
        <button
          className="btn btn-ghost btn-icon"
          style={{ width: 30, height: 30 }}
          onClick={onBack}
          aria-label="Back"
        >
          <window.I.ChevronLeft size={16} />
        </button>
        <div className="mobile-detail-title">Edit profile</div>
        <span style={{ width: 30 }} />
      </div>
      <div className="mobile-chrome-scroll mobile-profile-scroll">
        {window.ProfileScreen ? (
          <window.ProfileScreen profile={profile} onUpdateProfile={onUpdateProfile} demoProfile={profile?.id === "demo-signalleaf" ? profile._server || null : null} />
        ) : (
          <div className="mobile-empty">
            <div className="mobile-empty-title">Profile editor unavailable</div>
          </div>
        )}
      </div>
    </div>
  );
}

function MobilePricing({ onBack }) {
  return (
    <div className="mobile-chrome">
      <div className="mobile-detail-header">
        <button
          className="btn btn-ghost btn-icon"
          style={{ width: 30, height: 30 }}
          onClick={onBack}
          aria-label="Back"
        >
          <window.I.ChevronLeft size={16} />
        </button>
        <div className="mobile-detail-title">Pro plans</div>
        <span style={{ width: 30 }} />
      </div>
      <div className="mobile-chrome-scroll mobile-pricing-scroll">
        {window.PricingScreen ? (
          <window.PricingScreen onClose={onBack} />
        ) : (
          <div className="mobile-empty">
            <div className="mobile-empty-title">Pricing unavailable</div>
          </div>
        )}
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Tab bar
// ────────────────────────────────────────────────────────────────────────────

function MobileTabBar({ active, onNavigate, newCount = 0 }) {
  const items = [
    { id: "dashboard", label: "Home", icon: "Home" },
    { id: "opportunities", label: "Opportunities", icon: "Inbox", badge: newCount },
    { id: "scans", label: "Scans", icon: "Scan" },
    { id: "profile", label: "Profile", icon: "User" },
    { id: "settings", label: "Settings", icon: "Settings" },
  ];
  return (
    <div className="mobile-tabbar">
      {items.map((it) => {
        const Cmp = window.I[it.icon];
        const isActive = it.id === active;
        return (
          <button
            key={it.id}
            className={"mobile-tab " + (isActive ? "is-active" : "")}
            onClick={() => onNavigate?.(it.id)}
            aria-current={isActive ? "page" : undefined}
          >
            <div className="mobile-tab-icon">
              <Cmp size={20} />
              {it.badge > 0 && <span className="mobile-tab-badge">{it.badge}</span>}
            </div>
            <span className="mobile-tab-label">{it.label}</span>
          </button>
        );
      })}
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// MobileApp — the real mobile shell
// ────────────────────────────────────────────────────────────────────────────

function MobileApp({
  screen,
  onNavigate,
  state,
  setFeedState,
  selectedOpp,
  detailOpen,
  onOpenOpp,
  onCloseDetail,
  onFeedback,
  onDeleteOpportunity,
  onScanNow,
  isScanning,
  scanRefreshKey,
  onLogout,
  theme,
  onToggleTheme,
  profile,
  profiles,
  profilesLoading,
  onSwitchProfile,
  onNewProfile,
  isDemo,
  demoScans,
  demoScanResultsById,
  userEmail,
  newCount,
  activeProfileId,
  onRefreshProfiles,
  onRefreshOpps,
  onResultsCleared,
  onUpdateProfile,
  onDeleteAccount,
  onMakeYourOwn,
}) {
  const opps = state?.opps || [];

  const handleNavigate = (next) => {
    if (detailOpen) onCloseDetail?.();
    onNavigate?.(next);
  };

  let body;
  if (detailOpen && selectedOpp) {
    body = (
      <MobileDetail
        opp={selectedOpp}
        onBack={onCloseDetail}
        onFeedback={onFeedback}
        onDeleteOpportunity={onDeleteOpportunity}
        activeProfileId={activeProfileId}
        onNavigate={handleNavigate}
      />
    );
  } else {
    switch (screen) {
      case "opportunities":
        body = (
          <MobileFeed
            state={state}
            setState={setFeedState}
            onTap={onOpenOpp}
            latestScan={state?.latestScan}
          />
        );
        break;
      case "scans":
        body = (
          <MobileScans
            onScanNow={onScanNow}
            isScanning={isScanning}
            profile={profile}
            scanRefreshKey={scanRefreshKey}
            onOpenOpp={onOpenOpp}
            demoScans={demoScans}
            demoScanResultsById={demoScanResultsById}
          />
        );
        break;
      case "profile":
        body = (
          <MobileProfileEdit
            profile={profile}
            onUpdateProfile={onUpdateProfile}
            onBack={() => handleNavigate("settings")}
          />
        );
        break;
      case "settings":
        body = (
          <MobileSettings
            profile={profile}
            profiles={profiles}
            userEmail={userEmail}
            onLogout={onLogout}
            theme={theme}
            onToggleTheme={onToggleTheme}
            onSwitchProfile={onSwitchProfile}
            onNavigate={handleNavigate}
            activeProfileId={activeProfileId}
            onRefreshProfiles={onRefreshProfiles}
            onRefreshOpps={onRefreshOpps}
            onResultsCleared={onResultsCleared}
            onDeleteAccount={onDeleteAccount}
            isDemo={isDemo}
            onExitDemo={onMakeYourOwn}
          />
        );
        break;
      case "pricing":
        body = (
          <MobilePricing onBack={() => handleNavigate("settings")} />
        );
        break;
      case "dashboard":
      default:
        body = (
          <MobileDashboard
            opps={opps}
            profile={profile}
            profiles={profiles}
            profilesLoading={profilesLoading}
            onSwitchProfile={onSwitchProfile}
            onNewProfile={onNewProfile}
            isDemo={isDemo}
            latestScan={state?.latestScan}
            oppsLoading={state?.oppsLoading}
            onOpenOpp={onOpenOpp}
            onNavigate={handleNavigate}
            onScanNow={onScanNow}
            isScanning={isScanning}
            firstScan={state?.firstScan}
          />
        );
        break;
    }
  }

  const activeTab = ["dashboard", "opportunities", "scans", "profile", "settings"].includes(screen)
    ? screen
    : "settings";

  // Hide tab bar only on detail so top-level mobile sections stay reachable.
  const showTabBar = !(detailOpen && selectedOpp);

  return (
    <div className="mobile-app">
      <div className="mobile-app-body">{body}</div>
      {showTabBar && (
        <MobileTabBar active={activeTab} onNavigate={handleNavigate} newCount={newCount} />
      )}
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Desktop preview — the /mobile route
// ────────────────────────────────────────────────────────────────────────────

function IOSDevice({ width = 360, height = 780, children }) {
  return (
    <div className="ios-device" style={{ width, height }}>
      <div className="ios-device-notch" />
      <div className="ios-device-status">
        <span>9:41</span>
        <span className="ios-device-status-right">
          <span className="ios-device-bars" />
        </span>
      </div>
      <div className="ios-device-inner">{children}</div>
    </div>
  );
}
window.IOSDevice = IOSDevice;

function MobileScreen({ state, onOpenOpp, onFeedback }) {
  const { opps } = state;
  const [activeOpp, setActiveOpp] = React.useState(opps[0]);

  // Minimal stub setState for preview filter chips.
  const previewState = {
    ...state,
    query: state.query || "",
    sort: state.sort || "best",
    threshold: state.threshold || 0,
    highOnly: !!state.highOnly,
    subredditFilter: state.subredditFilter || "all",
    hideNotRelevant: !!state.hideNotRelevant,
    searchScope: state.searchScope || "all",
  };

  return (
    <div className="mobile-stage screen-enter">
      <Seo title="Mobile preview" noindex />
      <div style={{ width: "100%", maxWidth: 1200, margin: "0 auto" }}>
        <div className="page-header" style={{ marginBottom: 28 }}>
          <div>
            <h1 className="page-title">Mobile preview</h1>
            <p className="page-subtitle">
              How Community Ninja renders on a phone. Dashboard, opportunity feed, scans, and settings.
            </p>
          </div>
        </div>

        <div style={{ display: "flex", flexWrap: "wrap", gap: 36, justifyContent: "center" }}>
          <div className="mobile-card-preview">
            <div className="mobile-label">Dashboard</div>
            <IOSDevice width={360} height={780}>
              <MobileDashboard
                opps={opps}
                profile={state.profile}
                profiles={state.profile ? [state.profile] : []}
                profilesLoading={state.profilesLoading}
                latestScan={state.latestScan}
                onOpenOpp={() => {}}
                onNavigate={() => {}}
              />
              <MobileTabBar active="dashboard" />
            </IOSDevice>
          </div>
          <div className="mobile-card-preview">
            <div className="mobile-label">Opportunity feed</div>
            <IOSDevice width={360} height={780}>
              <MobileFeed
                state={previewState}
                setState={() => {}}
                onTap={(o) => setActiveOpp(o)}
                latestScan={state.latestScan}
              />
              <MobileTabBar active="opportunities" />
            </IOSDevice>
          </div>
          <div className="mobile-card-preview">
            <div className="mobile-label">Opportunity detail</div>
            <IOSDevice width={360} height={780}>
              <MobileDetail opp={activeOpp} onBack={() => {}} onFeedback={onFeedback} />
            </IOSDevice>
          </div>
          <div className="mobile-card-preview">
            <div className="mobile-label">Settings</div>
            <IOSDevice width={360} height={780}>
              <MobileSettings
                profile={state.profile}
                profiles={[]}
                userEmail=""
                onLogout={() => {}}
                theme="dark"
                onToggleTheme={() => {}}
                onSwitchProfile={() => {}}
                onNavigate={() => {}}
                activeProfileId=""
              />
              <MobileTabBar active="settings" />
            </IOSDevice>
          </div>
        </div>
      </div>
    </div>
  );
}

window.MobileApp = MobileApp;
window.MobileScreen = MobileScreen;
